import { useState } from "react";
import {
  Box, Card, Collapse, IconButton, Stack, Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import ContactsSection from "../ContactsSection";
import LocationsTab from "../LocationsTab";

interface Props {
  siteId: string;
  /** Optional override — defaults to collapsed (admin data is infrequent
   * once onboarding completes). */
  defaultOpen?: boolean;
}

/**
 * Collapsible wrapper around the site's contacts and locations editors.
 * Both sections are admin setup data the operator touches at onboarding
 * and then rarely after, so hiding them behind a single toggle keeps
 * the daily clinops view focused on the disease-area tabs below.
 */
export function SiteAdministrationPanel({ siteId, defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25,
          cursor: "pointer", userSelect: "none",
          borderBottom: open ? 1 : 0, borderColor: "divider",
        }}
        role="button"
        aria-expanded={open}
        aria-controls="site-administration-content"
      >
        <Typography sx={{ fontWeight: 600 }}>Site Administration</Typography>
        <Typography variant="caption" color="text.secondary">
          Key contacts · Locations
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" aria-label={open ? "Collapse" : "Expand"}>
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box id="site-administration-content" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <ContactsSection siteId={siteId} />
            <LocationsTab siteId={siteId} />
          </Stack>
        </Box>
      </Collapse>
    </Card>
  );
}
